import { useCallback } from 'react';
import { AlertPosition, AlertType } from '../enum/EnumRepository';
import { getAlertMessage } from '../constants/alertMessages';
import { useEditModeStore } from '../store/authStore';
import { useStandardAlertStore } from '../store/portal/alertStore';
import { useCheckBeforeMove } from './useCheckBeforeMove';

export type UnsavedChangesAction = () => void | Promise<void>;

interface ExitEditModeOptions {
  confirmWhenClean?: boolean;
}

export const scrollToChangedElement = () => {
  const changedElement = document.querySelector('[data-has-changed="true"]');
  changedElement?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
  });
};

export const useUnsavedChanges = () => {
  const { checkPageHasChanges } = useCheckBeforeMove();
  const setUnchangedAlert = useStandardAlertStore((s) => s.setUnchangedAlert);
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);

  const runAfterUnsavedChangesCheck = useCallback(
    (action: UnsavedChangesAction) => {
      if (!checkPageHasChanges()) {
        try {
          const result = action();
          if (result) {
            void result.catch((error) => {
              console.error('Unsaved changes action failed:', error);
            });
          }
        } catch (error) {
          console.error('Unsaved changes action failed:', error);
        }
        return;
      }

      setTimeout(scrollToChangedElement, 100);
      setUnchangedAlert(action);
    },
    [checkPageHasChanges, setUnchangedAlert],
  );

  const requestExitEditMode = useCallback(
    (options: ExitEditModeOptions = {}) => {
      const hasUnsavedChanges = checkPageHasChanges();
      const exitEditMode = () => {
        useEditModeStore.getState().setIsEditMode(false);
        if (hasUnsavedChanges) {
          window.location.reload();
        }
      };

      if (hasUnsavedChanges) {
        setTimeout(scrollToChangedElement, 100);
        setUnchangedAlert(exitEditMode);
        return;
      }

      if (options.confirmWhenClean) {
        enqueueAlert({
          type: AlertType.CONFIRM,
          position: AlertPosition.TOP,
          content: getAlertMessage('EXIT_EDIT_MODE'),
          callBack: exitEditMode,
        });
        return;
      }

      exitEditMode();
    },
    [checkPageHasChanges, enqueueAlert, setUnchangedAlert],
  );

  return {
    runAfterUnsavedChangesCheck,
    requestExitEditMode,
  };
};
